import { Alert } from '@chain-zen/ui'

import type { TExternalTrackingLinkSource } from '../../../types/tracking'
import ProviderIcon from '../provider-icon'
import { StatusIcon } from '../status-icon'
import { TrackingTimeDisplay } from '../tracking-time-display'

interface IExternalTrackingLinkInfo {
  trackingSourceData: TExternalTrackingLinkSource
}

/**
 * The component that displays the external tracking link info and its tooltip
 */
export const ExternalTrackingLinkInfo = (props: IExternalTrackingLinkInfo) => {
  const { trackingSourceData } = props
  const trackingSourceMetadata = trackingSourceData?.metadata

  const getTrackingStatus = () => {
    if (trackingSourceData?.locations?.length) {
      return 'Tracking'
    }
    if (trackingSourceData.metadata?.isSupported) {
      return 'Awaiting location'
    }
    return 'Not tracking'
  }

  const trackingStatus = getTrackingStatus()

  return (
    <div className="max-w-[400px]">
      <div className="flex w-full flex-col space-y-1 items-start">
        <ProviderIcon provider={trackingSourceMetadata?.provider || null} />

        <p className="font-semibold">{trackingSourceMetadata?.provider || 'Unsupported tracking link'}</p>

        {!!trackingSourceData?.locations?.length && trackingSourceData?.locations?.length > 0 && (
          <TrackingTimeDisplay
            filterReason={trackingSourceData.filterReason}
            lastLocationFetchedAt={trackingSourceData.lastLocationFetchedAt}
            lastLocationTimestamp={trackingSourceData.locations[trackingSourceData.locations.length - 1]?.timestamp}
            wrapper="p"
          />
        )}

        <div className="flex items-center gap-2">
          <StatusIcon isPositive={trackingSourceData.metadata?.isSupported ?? false} />
          <span>
            Provider:{' '}
            <span className="font-semibold">
              {trackingSourceData.metadata?.isSupported ? 'Supported' : 'Not supported'}
            </span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          <StatusIcon isPositive={Boolean(trackingSourceData.locations?.length)} />
          <span>
            Status: <span className="font-semibold">{trackingStatus}</span>
          </span>
        </div>

        <p className="max-w-[400px] line-clamp-4">{trackingSourceMetadata?.url}</p>

        {!trackingSourceMetadata?.isSupported && (
          <Alert
            variant="error"
            title="Link not supported"
            description={trackingSourceMetadata?.securityMessage}
            className="bg-gray-800 border-none"
            alertIcon={true}
            autoFade={false}
            centered={false}
          />
        )}

        {Boolean(trackingSourceMetadata?.isSupported) && !trackingSourceData?.locations?.length && (
          <Alert
            variant="error"
            title="Link not tracking yet"
            description="This link is a supported URL but has not started tracking yet. Either the link has expired or the tracking has not started yet. Please check back in 15 minutes."
            className="bg-background border-none"
            alertIcon={true}
            autoFade={false}
            centered={false}
          />
        )}
      </div>
    </div>
  )
}
